import React from 'react'
import {
  AdvisorData,
  FirmAdvisorType,
  NetworkAdvisorType,
  DirectAdvisorType,
  GeneralAdvisorData,
  RoboAdvisorType,
  RoboAdvisorData,
  RoboAdvisorAPIData
} from '../../utils/types'
import NetworkAdvisor from './NetworkAdvisor'
import FirmAdvisor from './FirmAdvisor'
import DirectAdvisor from './DirectAdvisor'
import DisclosureModule from '../DisclosureModule/DisclosureModule'
import RoboAdvisor from './RoboAdvisor'
import advisorLinks from '../../utils/appconfigs/RoboLinks'

interface AdvisorsBlockProps {
    advisorData: GeneralAdvisorData
}

const hasAdvisorMatchId = (advisorDataEntry: AdvisorData | RoboAdvisorData): advisorDataEntry is AdvisorData =>
  advisorDataEntry && 'advisorMatchId' in advisorDataEntry

const isRevisitedData = (advisorDataEntry: AdvisorData | RoboAdvisorData): boolean => {
  return !!advisorDataEntry?.isRevisit
}

const containsRoboAdvisor = (data: GeneralAdvisorData): data is RoboAdvisorData[] => {
  if (!data || data.length === 0) {
    return false
  }
  return !(hasAdvisorMatchId(data[0]))
}

const AdvisorsBlock = ({ advisorData }: AdvisorsBlockProps) => {
  if (!advisorData || advisorData.length === 0) {
    return null
  }

  return (
        <>
            <div className='advisors_block'>
                {advisorData.map((advisorDataEntry, index) => {
                  if (hasAdvisorMatchId(advisorDataEntry) || isRevisitedData(advisorDataEntry)) {
                    const regularEntry = advisorDataEntry as AdvisorData

                    if (regularEntry.advisor.format === 'human') {
                      const enhancedAdvisor = { ...regularEntry.advisor, distance: regularEntry.distance }
                      return (
                                <DirectAdvisor
                                    position={index}
                                    key={`${regularEntry.advisor.id}-human`}
                                    advisor={enhancedAdvisor as DirectAdvisorType}
                                />
                      )
                    }

                    switch (regularEntry.advisor.format) {
                      case 'network':
                        return (
                                    <NetworkAdvisor
                                        position={index}
                                        key={`${regularEntry.advisor.id}-network`}
                                        advisor={regularEntry.advisor as NetworkAdvisorType}
                                    />
                        )
                      case 'firm':
                        return (
                                    <FirmAdvisor
                                        position={index}
                                        key={`${regularEntry.advisor.id}-firm`}
                                        advisor={regularEntry.advisor as FirmAdvisorType}
                                    />
                        )
                      case 'roboadvisor': {
                        const advisorData = regularEntry.advisor as RoboAdvisorAPIData
                        const networkData = advisorData.network || {}
                        const roboAdvisor = {
                          ...advisorData,
                          compensation: advisorData.compensation || '',
                          certifications: advisorData.certifications || '',
                          ctaDestination: advisorLinks[advisorData.id.toString()] || advisorData.cta_destination || '',
                          fallback: advisorData.fallback || false,
                          network: {
                            ...networkData,
                            descriptionHtml: networkData.description_html || '',
                            matchProductType: networkData.match_product_type || '',
                            foundedIn: networkData.founded_in || ''
                          }
                        } as unknown as RoboAdvisorType

                        return (
                                    <RoboAdvisor
                                        advisor={roboAdvisor}
                                        key={`${regularEntry.advisor.id}-robo`}
                                    />
                        )
                      }
                      default:
                        return null
                    }
                  } else {
                    const roboEntry = advisorDataEntry as RoboAdvisorData
                    const advisorData = roboEntry.advisor as RoboAdvisorAPIData
                    const networkData = advisorData.network || {}

                    const mappedAdvisor = {
                      ...advisorData,
                      ctaDestination: advisorData.cta_destination || advisorLinks[advisorData.id?.toString()] || '',
                      network: {
                        ...networkData,
                        descriptionHtml: networkData.description_html || '',
                        matchProductType: networkData.match_product_type || '',
                        founded_in: networkData.founded_in || ''
                      }
                    } as RoboAdvisorType

                    return (
                            <RoboAdvisor
                                advisor={mappedAdvisor}
                                key={`${roboEntry.advisor.id}-robo`}
                            />
                    )
                  }
                })}
            </div>
            {containsRoboAdvisor(advisorData) ? null : <DisclosureModule advisors={advisorData as AdvisorData[]} />}
        </>
  )
}

export default AdvisorsBlock
